import React, { useState } from 'react';
import { Container } from '@/components/ui/container';
import { Stack } from '@/components/ui/layout';
import { Heading, Text } from '@/components/ui/typography';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { getColor } from '@/config/theme';

export default function FrequentlyAskedQuestions() {
  const [activeTab, setActiveTab] = useState('candidate');

  // Candidate FAQs
  const candidateFAQs = [
    { id: 'candidate-1', question: "How do I prepare for an interview with AI Evaluator?", answer: "All you need is a solid internet connection and a quiet area to perform your best." },
    { id: 'candidate-2', question: "What help can I get?", answer: "We offer extensive support through our help center and customer service team." },
    { id: 'candidate-3', question: "What can I do myself?", answer: "You can manage your profile, schedule interviews, and practice with AI Evaluator." },
    { id: 'candidate-4', question: "What languages does AI Evaluator support?", answer: "It supports English, Spanish, French, and German." }
  ];

  // Company FAQs
  const companyFAQs = [
    { id: 'company-1', question: "How do I post a job listing?", answer: "Log in to your employer dashboard and fill in the job details to post your listing." },
    { id: 'company-2', question: "How do I evaluate candidates?", answer: "Use AI-powered insights to assess candidates based on their performance and skills." },
    { id: 'company-3', question: "What are the pricing plans?", answer: "We offer various pricing plans based on company needs. Visit our pricing page for details." },
    { id: 'company-4', question: "How can I schedule interviews?", answer: "You can schedule interviews directly from your dashboard and manage candidates efficiently." }
  ];

  // Render correct FAQ list based on active tab
  const faqsToRender = activeTab === 'candidate' ? candidateFAQs : companyFAQs;

  return (
    <section id="faq-section" className="py-12 md:py-16 lg:py-20 bg-background">
      <Container>
        <Stack spacing="xl" align="center" className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center w-full">
            <Heading level="h1" className="text-3xl md:text-4xl font-bold mb-4">
              Frequently Asked Questions
            </Heading>
            <Text variant="muted" className="text-lg">
              Your go-to guide for quick and accurate information.
            </Text>
          </div>

          {/* Tabs */}
          <div className="flex gap-4 justify-center">
            <Button
              variant={activeTab === 'candidate' ? 'default' : 'outline'}
              onClick={() => setActiveTab('candidate')}
              style={activeTab === 'candidate' ? {
                backgroundColor: getColor('primary'),
                color: getColor('primaryForeground'),
              } : {}}
              className="rounded-full"
            >
              Candidate
            </Button>
            <Button
              variant={activeTab === 'company' ? 'default' : 'outline'}
              onClick={() => setActiveTab('company')}
              style={activeTab === 'company' ? {
                backgroundColor: getColor('primary'),
                color: getColor('primaryForeground'),
              } : {}}
              className="rounded-full"
            >
              Company
            </Button>
          </div>

          {/* FAQ Accordion */}
          <div className="w-full">
            <Accordion type="single" collapsible className="w-full space-y-4">
              {faqsToRender.map((faq) => (
                <AccordionItem
                  key={faq.id}
                  value={faq.id}
                  className={cn(
                    "border border-border rounded-lg bg-secondary/30",
                    "px-4 data-[state=open]:bg-secondary/50"
                  )}
                >
                  <AccordionTrigger className="text-left font-semibold hover:no-underline">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent>
                    <Text variant="muted" className="pt-2">
                      {faq.answer}
                    </Text>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </Stack>
      </Container>
    </section>
  );
}
